import React, { useCallback, useEffect, useState } from 'react';
import useStore from '../store/useStore';
import { alertsAPI, metricsAPI, reportsAPI } from '../services/api';
import useModalOverlayClose from '../hooks/useModalOverlayClose';
import { describeAlert } from './AlertBanner';
import '../styles/KpiManagement.css';
import '../styles/Alerts.css';

const OPERATORS = ['>', '>=', '<', '<=', '=', '!='];
const SEVERITIES = ['info', 'warning', 'critical'];
const PERIODS = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'year', label: 'This year' },
];
const DURATIONS = [
  { value: 0, label: 'Immediately' },
  { value: 60, label: 'For 1 minute' },
  { value: 300, label: 'For 5 minutes' },
  { value: 600, label: 'For 10 minutes' },
  { value: 1800, label: 'For 30 minutes' },
  { value: 3600, label: 'For 1 hour' },
];

const EMPTY = {
  name: '', kind: 'live', metricKey: '', measure: '', timeDimension: '', period: 'week',
  operator: '>', threshold: '', durationSeconds: 0, severity: 'warning', recipients: '', isActive: true,
};

const STATE_LABEL = { ok: 'OK', pending: 'Pending', firing: 'Firing' };

/**
 * Admin page: alert rules over live metrics and Cube measures, their current
 * state, and the recent fired / resolved / acknowledged history.
 */
const AlertsPage = () => {
  const currentCompany = useStore((state) => state.currentCompany);
  const user = useStore((state) => state.user);
  const setAlerts = useStore((state) => state.setAlerts);
  const companyId = currentCompany?.id;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [cubes, setCubes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const overlay = useModalOverlayClose(() => setEditing(null));

  const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError('');
      const [r, e, m, meta] = await Promise.all([
        alertsAPI.rules(companyId),
        alertsAPI.events(companyId, 30),
        metricsAPI.getDefinitions(companyId).catch(() => null),
        reportsAPI.getMeta(companyId).catch(() => null),
      ]);
      if (r?.success) {
        setRules(r.data || []);
        setAlerts((r.data || []).filter((x) => x.state === 'firing' && x.isActive));
      }
      if (e?.success) setEvents(e.data || []);
      if (m?.success) setMetrics(m.data || []);
      if (meta?.success) setCubes((meta.data?.cubes || []).filter((c) => c.meta?.kind !== 'metric'));
    } catch (err) {
      setError(describeError(err, 'Failed to load alerts'));
    } finally {
      setLoading(false);
    }
  }, [companyId, setAlerts]);

  useEffect(() => { load(); }, [load]);

  const measures = cubes.flatMap((c) => (c.measures || []).map((m) => ({ ...m, cube: c.name })));
  const selectedCube = editing?.measure ? editing.measure.split('.')[0] : null;
  const timeDimensions = cubes
    .filter((c) => !selectedCube || c.name === selectedCube)
    .flatMap((c) => (c.dimensions || []).filter((d) => d.type === 'time'));

  const openCreate = () => setEditing({ ...EMPTY });
  const openEdit = (rule) => setEditing({
    ...EMPTY,
    ...rule,
    metricKey: rule.metricKey || '',
    measure: rule.measure || '',
    timeDimension: rule.timeDimension || '',
    period: rule.period || 'week',
    threshold: String(rule.threshold ?? ''),
    recipients: (rule.recipients || []).join(', '),
  });
  const update = (patch) => setEditing((f) => ({ ...f, ...patch }));

  const save = async (e) => {
    e.preventDefault();
    const body = {
      name: editing.name,
      kind: editing.kind,
      operator: editing.operator,
      threshold: editing.threshold === '' ? null : Number(editing.threshold),
      severity: editing.severity,
      recipients: editing.recipients.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean),
      isActive: editing.isActive,
      ...(editing.kind === 'live'
        ? { metricKey: editing.metricKey, durationSeconds: Number(editing.durationSeconds) || 0 }
        : { measure: editing.measure, timeDimension: editing.timeDimension || null, period: editing.period }),
    };
    try {
      setSaving(true);
      setError('');
      const res = editing.id ? await alertsAPI.update(editing.id, companyId, body) : await alertsAPI.create(companyId, body);
      if (res?.success) {
        setMessage(editing.id ? `Updated "${res.data.name}"` : `Created "${res.data.name}"`);
        setEditing(null);
        await load();
      }
    } catch (err) {
      setError(describeError(err, 'Failed to save alert rule'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete the alert rule "${rule.name}"?`)) return;
    try {
      await alertsAPI.delete(rule.id, companyId);
      setMessage(`Deleted "${rule.name}"`);
      await load();
    } catch (err) {
      setError(describeError(err, 'Failed to delete'));
    }
  };

  const evaluate = async (rule) => {
    try {
      const res = await alertsAPI.evaluate(rule.id, companyId);
      const r = res?.data?.rule;
      setMessage(r?.lastError ? `Evaluation failed: ${r.lastError}` : `${rule.name}: ${describeAlert(r || rule)} → ${STATE_LABEL[r?.state] || r?.state}`);
      await load();
    } catch (err) {
      setError(describeError(err, 'Failed to evaluate'));
    }
  };

  const acknowledge = async (rule) => {
    try {
      await alertsAPI.acknowledge(rule.id, companyId);
      await load();
    } catch (err) {
      setError(describeError(err, 'Failed to acknowledge'));
    }
  };

  return (
    <div className="kpi-management alerts-page">
      <div className="page-header">
        <div>
          <h2>Alerts</h2>
          {currentCompany && <p className="page-subtitle">Threshold alerts for <strong>{currentCompany.name}</strong> on live metrics (checked on every push) and dataset measures (checked after each sync)</p>}
        </div>
        <div className="page-actions">
          <button className="btn-secondary" onClick={load}>Refresh</button>
          {isAdmin && <button className="btn-primary" onClick={openCreate} disabled={!companyId}>+ New alert rule</button>}
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}
      {message && <div className="success-message" style={{ marginBottom: '1rem' }}>{message}<button className="error-dismiss" onClick={() => setMessage('')} aria-label="Dismiss">×</button></div>}

      {loading ? <div className="loading-spinner" /> : (
        <>
          <table className="kpi-table alerts-table">
            <thead>
              <tr><th>Rule</th><th>Watches</th><th>Condition</th><th>Severity</th><th>State</th><th>Last value</th><th /></tr>
            </thead>
            <tbody>
              {rules.length === 0 && <tr><td colSpan={7} className="dataset-empty-state">No alert rules yet.</td></tr>}
              {rules.map((rule) => (
                <tr key={rule.id} className={!rule.isActive ? 'alerts-row--inactive' : undefined}>
                  <td>
                    <strong>{rule.name}</strong>
                    <div className="kpi-key">{(rule.recipients || []).length ? `emails ${rule.recipients.join(', ')}` : 'no email recipients'}</div>
                  </td>
                  <td>
                    <span className={`dataset-badge ${rule.kind === 'live' ? 'info' : 'ok'}`} style={{ marginLeft: 0 }}>{rule.kind === 'live' ? 'live metric' : 'measure'}</span>
                    <div className="kpi-key"><code>{rule.kind === 'live' ? rule.metricKey : rule.measure}</code></div>
                  </td>
                  <td>
                    {rule.operator} {Number(rule.threshold).toLocaleString()}
                    <div className="kpi-key">
                      {rule.kind === 'live'
                        ? (rule.durationSeconds ? `for ${Math.round(rule.durationSeconds / 60) || rule.durationSeconds + 's'} min` : 'immediately')
                        : (PERIODS.find((p) => p.value === rule.period)?.label.toLowerCase() || rule.period)}
                    </div>
                  </td>
                  <td><span className={`alert-severity alert-severity--${rule.severity}`}>{rule.severity}</span></td>
                  <td>
                    <span className={`alert-state alert-state--${rule.isActive ? rule.state : 'off'}`}>{rule.isActive ? STATE_LABEL[rule.state] : 'Disabled'}</span>
                    {rule.state === 'firing' && rule.acknowledgedAt && <div className="kpi-key">acknowledged</div>}
                    {rule.lastError && <div className="kpi-key alerts-error" title={rule.lastError}>error: {rule.lastError}</div>}
                  </td>
                  <td>{rule.lastValue === null || rule.lastValue === undefined ? '—' : Number(rule.lastValue).toLocaleString()}</td>
                  <td className="alerts-actions">
                    {rule.state === 'firing' && !rule.acknowledgedAt && <button className="btn-secondary-small" onClick={() => acknowledge(rule)}>Acknowledge</button>}
                    {isAdmin && rule.kind === 'measure' && <button className="btn-secondary-small" onClick={() => evaluate(rule)}>Check now</button>}
                    {isAdmin && <button className="btn-secondary-small" onClick={() => openEdit(rule)}>Edit</button>}
                    {isAdmin && <button className="btn-danger-small" onClick={() => remove(rule)}>Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="alerts-history-title">Recent activity</h3>
          {events.length === 0 ? <p className="dataset-empty">No alerts have fired yet.</p> : (
            <ul className="alerts-history">
              {events.map((ev) => (
                <li key={ev.id} className={`alerts-history-item alerts-history-item--${ev.event}`}>
                  <span className="alerts-history-time">{new Date(ev.createdAt).toLocaleString()}</span>
                  <span className={`alert-state alert-state--${ev.event === 'fired' ? 'firing' : ev.event === 'resolved' ? 'ok' : 'pending'}`}>{ev.event}</span>
                  <strong>{ev.ruleName}</strong>
                  <span>{ev.message}</span>
                  {ev.username && <span className="kpi-key">by {ev.username}</span>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {editing && (
        <div className="modal-overlay" {...overlay}>
          <div className="modal-content kpi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing.id ? 'Edit alert rule' : 'New alert rule'}</h2>
              <button onClick={() => setEditing(null)} className="modal-close" aria-label="Close">&times;</button>
            </div>
            <form onSubmit={save}>
              <div className="form-group">
                <label htmlFor="alert-name">Name</label>
                <input id="alert-name" value={editing.name} onChange={(e) => update({ name: e.target.value })} placeholder="e.g. Despatch queue too long" required />
              </div>
              <div className="form-group">
                <label htmlFor="alert-kind">Watches</label>
                <select id="alert-kind" value={editing.kind} onChange={(e) => update({ kind: e.target.value })} disabled={!!editing.id}>
                  <option value="live">A live metric (checked on every push)</option>
                  <option value="measure">A dataset measure (checked after each sync)</option>
                </select>
              </div>
              {editing.kind === 'live' ? (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="alert-metric">Metric</label>
                    <select id="alert-metric" value={editing.metricKey} onChange={(e) => update({ metricKey: e.target.value })} required>
                      <option value="">Choose a metric…</option>
                      {metrics.map((m) => <option key={m.metric_key} value={m.metric_key}>{m.display_name || m.metric_key}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="alert-duration">Condition must hold</label>
                    <select id="alert-duration" value={editing.durationSeconds} onChange={(e) => update({ durationSeconds: Number(e.target.value) })}>
                      {DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="alert-measure">Measure</label>
                    <select id="alert-measure" value={editing.measure} onChange={(e) => update({ measure: e.target.value, timeDimension: '' })} required>
                      <option value="">Choose a measure…</option>
                      {measures.map((m) => <option key={m.name} value={m.name}>{m.cube} · {m.shortTitle || m.title || m.name}</option>)}
                    </select>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="alert-time">Time dimension</label>
                      <select id="alert-time" value={editing.timeDimension} onChange={(e) => update({ timeDimension: e.target.value })}>
                        <option value="">None (all rows)</option>
                        {timeDimensions.map((d) => <option key={d.name} value={d.name}>{d.shortTitle || d.title || d.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="alert-period">Period</label>
                      <select id="alert-period" value={editing.period} onChange={(e) => update({ period: e.target.value })} disabled={!editing.timeDimension}>
                        {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="alert-operator">Fire when the value is</label>
                  <select id="alert-operator" value={editing.operator} onChange={(e) => update({ operator: e.target.value })}>
                    {OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="alert-threshold">Threshold</label>
                  <input id="alert-threshold" type="number" step="any" value={editing.threshold} onChange={(e) => update({ threshold: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label htmlFor="alert-severity">Severity</label>
                  <select id="alert-severity" value={editing.severity} onChange={(e) => update({ severity: e.target.value })}>
                    {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="alert-recipients">Email recipients</label>
                <input id="alert-recipients" value={editing.recipients} onChange={(e) => update({ recipients: e.target.value })} placeholder="ops@example.com, manager@example.com" />
                <small>Emailed when the alert fires and when it resolves. Leave empty for on-screen only.</small>
              </div>
              <label className="member-option" style={{ alignItems: 'center' }}>
                <input type="checkbox" checked={editing.isActive} onChange={(e) => update({ isActive: e.target.checked })} />
                <span>Active</span>
              </label>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : editing.id ? 'Save rule' : 'Create rule'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsPage;
